const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const money = value =>
  '\u20a6' +
  Number(value || 0).toLocaleString(
    'en-NG',
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }
  );

const displayDate = value => {
  if (!value) return '';

  const parsed = new Date(
    String(value).slice(0, 10) +
      'T00:00:00'
  );

  if (Number.isNaN(parsed.getTime())) {
    return escapeHtml(value);
  }

  return escapeHtml(
    parsed.toLocaleDateString(
      'en-NG',
      {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }
    )
  );
};

const safeLogoUrl = value => {
  if (!value) return '';

  try {
    const url = new URL(
      value,
      window.location.origin
    );

    if (
      !['http:', 'https:'].includes(
        url.protocol
      )
    ) {
      return '';
    }

    return escapeHtml(url.href);
  } catch (_) {
    return '';
  }
};

const safeSignatureDataUrl = value => {
  const candidate =
    String(value || '').trim();

  if (
    !candidate ||
    candidate.length > 3 * 1024 * 1024 ||
    !/^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(
      candidate
    )
  ) {
    return '';
  }

  return candidate;
};

export const buildPurchaseOrderHtml = (
  po,
  lines = [],
  settings = {}
) => {
  const logoUrl =
    safeLogoUrl(settings.logo_url);

  const signatureUrl =
    safeSignatureDataUrl(
      po.company_signature_data_url
    );

  const status =
    String(po.status || 'draft')
      .toUpperCase();

  const watermark =
    ['DRAFT', 'CANCELLED'].includes(status)
      ? `<div class="watermark">${escapeHtml(status)}</div>`
      : '';

  const totalAmount =
    lines.reduce(
      (sum, line) => {
        const quantity =
          Number(line.quantity || 0);

        const unitCost =
          Number(line.unit_cost || 0);

        return (
          sum +
          Number(
            line.line_total ||
            quantity * unitCost
          )
        );
      },
      0
    );

  const supplierContact = [
    po.supplier_address,
    po.supplier_phone,
    po.supplier_email
  ]
    .filter(Boolean)
    .map(
      value =>
        `<div>${escapeHtml(value)}</div>`
    )
    .join('');

  const itemRows = lines
    .map((line, index) => {
      const description =
        line.description ||
        line.material_name ||
        line.product_name ||
        'Item';

      const quantity =
        Number(line.quantity || 0);

      const unitCost =
        Number(line.unit_cost || 0);

      const lineTotal =
        Number(
          line.line_total ||
          quantity * unitCost
        );

      return `
        <tr>
          <td class="centre">${index + 1}</td>
          <td>${escapeHtml(description)}</td>
          <td class="centre">${escapeHtml(line.unit_of_measure || '')}</td>
          <td class="number">${escapeHtml(quantity.toLocaleString('en-NG'))}</td>
          <td class="number">${escapeHtml(money(unitCost))}</td>
          <td class="number">${escapeHtml(money(lineTotal))}</td>
        </tr>
      `;
    })
    .join('');

  const companyName =
    settings.company_name ||
    'Bloans Creations Ltd';

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(po.po_number)} | Purchase Order</title>
  <style>
    @page {
      size: A4;
      margin: 12mm;
    }

    * {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body {
      margin: 0;
      background: #ececec;
      color: #2d2824;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 12px;
      line-height: 1.45;
    }

    .toolbar {
      position: sticky;
      top: 0;
      z-index: 5;
      display: flex;
      justify-content: center;
      gap: 10px;
      padding: 12px;
      background: #3b2a21;
    }

    .toolbar button {
      border: 0;
      border-radius: 5px;
      padding: 10px 16px;
      cursor: pointer;
      font-weight: 700;
    }

    .print-button {
      background: #a8893d;
      color: #ffffff;
    }

    .close-button {
      background: #ffffff;
      color: #3b2a21;
    }

    .page {
      position: relative;
      width: 210mm;
      min-height: 297mm;
      margin: 16px auto;
      padding: 14mm;
      overflow: hidden;
      background: #ffffff;
      box-shadow: 0 4px 22px rgba(0,0,0,.16);
    }

    .watermark {
      position: absolute;
      top: 42%;
      left: 8%;
      width: 84%;
      transform: rotate(-28deg);
      color: rgba(143,46,36,.09);
      font-size: 88px;
      font-weight: 800;
      letter-spacing: 10px;
      text-align: center;
      pointer-events: none;
    }

    .header {
      display: flex;
      justify-content: space-between;
      gap: 28px;
      padding-bottom: 18px;
      border-bottom: 3px solid #556b2f;
    }

    .brand {
      display: flex;
      align-items: flex-start;
      gap: 14px;
      max-width: 58%;
    }

    .logo {
      width: 72px;
      max-height: 72px;
      object-fit: contain;
    }

    .company-name {
      color: #556b2f;
      font-family: Georgia, serif;
      font-size: 22px;
      font-weight: 700;
    }

    .company-detail {
      margin-top: 4px;
      color: #5f5a55;
      font-size: 11px;
    }

    .document-heading {
      text-align: right;
    }

    .document-title {
      color: #a8893d;
      font-family: Georgia, serif;
      font-size: 26px;
      font-weight: 700;
      letter-spacing: 1px;
    }

    .po-number {
      margin-top: 4px;
      color: #3b2a21;
      font-size: 15px;
      font-weight: 700;
    }

    .status {
      display: inline-block;
      margin-top: 8px;
      padding: 4px 9px;
      border-radius: 20px;
      background: #f2efe7;
      color: #556b2f;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 1px;
    }

    .information {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 26px;
      margin: 24px 0;
    }

    .section-label {
      margin-bottom: 7px;
      color: #8a8a8a;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 1.2px;
      text-transform: uppercase;
    }

    .supplier-name {
      color: #3b2a21;
      font-size: 15px;
      font-weight: 700;
    }

    .supplier-contact {
      margin-top: 5px;
      color: #55504b;
      line-height: 1.55;
    }

    .detail-row {
      display: grid;
      grid-template-columns: 125px 1fr;
      margin-bottom: 5px;
    }

    .detail-label {
      color: #77716b;
    }

    .detail-value {
      color: #3b2a21;
      font-weight: 600;
    }

    table {
      width: 100%;
      margin-top: 8px;
      border-collapse: collapse;
    }

    thead {
      display: table-header-group;
    }

    tr {
      page-break-inside: avoid;
    }

    th {
      padding: 10px 8px;
      background: #556b2f;
      color: #ffffff;
      font-size: 9px;
      letter-spacing: .7px;
      text-align: left;
      text-transform: uppercase;
    }

    td {
      padding: 10px 8px;
      border-bottom: 1px solid #ded8ca;
      vertical-align: top;
    }

    tbody tr:nth-child(even) td {
      background: #f8f6f0;
    }

    .centre {
      text-align: center;
    }

    .number {
      white-space: nowrap;
      text-align: right;
    }

    .total-row td {
      padding-top: 13px;
      padding-bottom: 13px;
      background: #f2efe7 !important;
      color: #3b2a21;
      font-size: 13px;
      font-weight: 700;
    }

    .terms {
      margin-top: 20px;
      page-break-inside: avoid;
    }

    .term-box {
      margin-top: 8px;
      padding: 11px 13px;
      border-left: 3px solid #a8893d;
      background: #faf7ef;
    }

    .signatures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 44px;
      margin-top: 45px;
      page-break-inside: avoid;
    }

    .signature {
      padding-top: 7px;
      border-top: 1px solid #3b2a21;
      color: #66605a;
      font-size: 10px;
    }

    .footer {
      margin-top: 28px;
      padding-top: 10px;
      border-top: 1px solid #ded8ca;
      color: #77716b;
      font-size: 9px;
      text-align: center;
    }

    @media print {
      body {
        background: #ffffff;
      }

      .no-print {
        display: none !important;
      }

      .page {
        width: auto;
        min-height: auto;
        margin: 0;
        padding: 0;
        box-shadow: none;
      }
    }
  </style>
</head>
<body>
  <div class="toolbar no-print">
    <button
      class="print-button"
      onclick="window.print()"
    >
      Print or Save as PDF
    </button>

    <button
      class="close-button"
      onclick="window.close()"
    >
      Close
    </button>
  </div>

  <main class="page">
    ${watermark}

    <header class="header">
      <div class="brand">
        ${logoUrl
          ? `<img class="logo" src="${logoUrl}" alt="Company logo">`
          : ''}

        <div>
          <div class="company-name">
            ${escapeHtml(companyName)}
          </div>

          <div class="company-detail">
            ${escapeHtml(settings.company_address || '')}
          </div>

          <div class="company-detail">
            ${escapeHtml(settings.company_phone || '')}
          </div>

          <div class="company-detail">
            ${escapeHtml(settings.company_website || '')}
          </div>
        </div>
      </div>

      <div class="document-heading">
        <div class="document-title">
          PURCHASE ORDER
        </div>

        <div class="po-number">
          ${escapeHtml(po.po_number)}
        </div>

        <div class="status">
          ${escapeHtml(status)}
        </div>
      </div>
    </header>

    <section class="information">
      <div>
        <div class="section-label">Supplier</div>

        <div class="supplier-name">
          ${escapeHtml(po.supplier_name)}
        </div>

        <div class="supplier-contact">
          ${supplierContact ||
            'No supplier contact information recorded'}
        </div>
      </div>

      <div>
        <div class="section-label">
          Order Details
        </div>

        <div class="detail-row">
          <div class="detail-label">PO Date</div>
          <div class="detail-value">
            ${displayDate(po.date)}
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">
            Expected Delivery
          </div>
          <div class="detail-value">
            ${displayDate(po.expected_date) ||
              'Not specified'}
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">
            Prepared By
          </div>
          <div class="detail-value">
            ${escapeHtml(po.created_by_name || '')}
          </div>
        </div>

        <div class="detail-row">
          <div class="detail-label">
            Approved By
          </div>
          <div class="detail-value">
            ${escapeHtml(
              po.approved_by_name ||
              'Pending approval'
            )}
          </div>
        </div>
      </div>
    </section>

    <table>
      <thead>
        <tr>
          <th class="centre">#</th>
          <th>Description</th>
          <th class="centre">Unit</th>
          <th class="number">Quantity</th>
          <th class="number">Unit Price</th>
          <th class="number">Amount</th>
        </tr>
      </thead>

      <tbody>
        ${itemRows}

        <tr class="total-row">
          <td colspan="5" class="number">
            Total Amount
          </td>
          <td class="number">
            ${escapeHtml(money(totalAmount))}
          </td>
        </tr>
      </tbody>
    </table>

    <section class="terms">
      ${settings.po_delivery_address
        ? `<div class="term-box">
             <strong>Deliver To:</strong>
             ${escapeHtml(settings.po_delivery_address)}
           </div>`
        : ''}

      ${settings.po_payment_terms
        ? `<div class="term-box">
             <strong>Payment Terms:</strong>
             ${escapeHtml(settings.po_payment_terms)}
           </div>`
        : ''}

      ${(po.notes || settings.po_notes)
        ? `<div class="term-box">
             <strong>Notes:</strong>
             ${escapeHtml(
               po.notes || settings.po_notes
             )}
           </div>`
        : ''}

      ${settings.po_authorised_by
        ? `<div class="term-box">
             <strong>Authorised By:</strong>
             ${escapeHtml(settings.po_authorised_by)}
           </div>`
        : ''}
    </section>

    <section class="signatures">
      <div class="signature">
        ${signatureUrl ? `
          <div style="font-size:10px;color:#777;margin-bottom:4px">
            Electronically authorised by
          </div>
          <img
            src="${signatureUrl}"
            alt="Managing Director signature"
            style="display:block;width:190px;max-height:90px;object-fit:contain;object-position:left center;margin:0 0 5px"
          >
          <div style="font-weight:700">
            ${escapeHtml(
              po.company_signer_name ||
              po.approved_by_name ||
              ''
            )}
          </div>
          <div style="font-size:10px;color:#777">
            ${escapeHtml(
              po.company_signer_title ||
              'Managing Director'
            )}
          </div>
          <div style="font-size:10px;color:#777">
            ${displayDate(
              po.company_signed_at
            )}
          </div>
        ` : `
          Approved for Bloans Creations Ltd.
        `}
      </div>

      <div class="signature">
        Supplier acknowledgement and date
      </div>
    </section>

    <footer class="footer">
      This purchase order was issued by
      ${escapeHtml(companyName)}.
    </footer>
  </main>
</body>
</html>`;
};

export const printPurchaseOrder = (
  po,
  lines = [],
  settings = {}
) => {
  const printWindow =
    window.open('', '_blank');

  if (!printWindow) {
    window.alert(
      'The print window was blocked. Allow pop-ups for BloansBooks and try again.'
    );
    return;
  }

  printWindow.opener = null;
  printWindow.document.open();
  printWindow.document.write(
    buildPurchaseOrderHtml(
      po,
      lines,
      settings
    )
  );
  printWindow.document.close();
  printWindow.focus();
};
